import * as React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";

interface InteractiveCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  enableTilt?: boolean;
  enablePulse?: boolean;
  enableDynamicShadow?: boolean;
}

export const InteractiveCard = React.forwardRef<HTMLDivElement, InteractiveCardProps>(
  ({ 
    children, 
    className, 
    enableTilt = true, 
    enablePulse = true, 
    enableDynamicShadow = true,
    ...props 
  }, ref) => {
    const cardRef = React.useRef<HTMLDivElement>(null);
    const [isMobile, setIsMobile] = React.useState(false);
    
    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    // Mouse position tracking
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    
    // Smooth spring animations for tilt
    const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [5, -5]), {
      stiffness: 150,
      damping: 20
    });
    const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-5, 5]), {
      stiffness: 150,
      damping: 20
    });
    
    // Dynamic shadow based on mouse position
    const shadowX = useSpring(useTransform(mouseX, [-0.5, 0.5], [-15, 15]), {
      stiffness: 100,
      damping: 15
    });
    const shadowY = useSpring(useTransform(mouseY, [-0.5, 0.5], [-15, 15]), {
      stiffness: 100,
      damping: 15
    });
    
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!cardRef.current || !enableTilt || isMobile) return;
      
      const rect = cardRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      // Normalize mouse position (-0.5 to 0.5)
      const x = (e.clientX - centerX) / rect.width;
      const y = (e.clientY - centerY) / rect.height;
      
      mouseX.set(x);
      mouseY.set(y);
    };
    
    const handleMouseLeave = () => {
      mouseX.set(0);
      mouseY.set(0);
    };
    
    // Disable tilt and dynamic shadow on mobile
    const shouldEnableTilt = enableTilt && !isMobile;
    const shouldEnableDynamicShadow = enableDynamicShadow && !isMobile;
    
    return (
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX: shouldEnableTilt ? rotateX : 0,
          rotateY: shouldEnableTilt ? rotateY : 0,
          transformStyle: "preserve-3d",
        }}
        className={cn(
          "group relative rounded-lg border bg-card text-card-foreground shadow-sm w-full overflow-hidden isolate",
          "will-change-transform transition-all duration-300 ease-out",
          // Subtler hover effects
          isMobile ? "hover:scale-[1.005]" : "hover:scale-[1.01]",
          className
        )}
      >
        {/* Subtle Pulsing Border Glow - Only on Hover */}
        {enablePulse && (
          <motion.div
            className="absolute -inset-[1px] rounded-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none -z-10"
            style={{
              background: "linear-gradient(135deg, hsl(var(--accent) / 0.15), hsl(var(--accent) / 0.05), hsl(var(--accent) / 0.15))",
              backgroundSize: "200% 200%",
            }}
            animate={{
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        )}
        
        {/* Subtle Dynamic Shadow - Only on Hover */}
        {shouldEnableDynamicShadow && (
          <motion.div
            className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-50 transition-opacity duration-400 pointer-events-none blur-xl -z-10"
            style={{
              x: shadowX,
              y: shadowY,
              background: "radial-gradient(circle at center, hsl(var(--accent) / 0.08), transparent 70%)",
            }}
          />
        )}
        
        {/* Card Content with subtle 3D transform */}
        <div className="relative z-10" style={{ transform: shouldEnableTilt ? "translateZ(15px)" : undefined }}>
          {children}
        </div>
        
        {/* Subtle Corner Accent Glow - Only on Hover */}
        <div className="absolute top-0 right-0 w-12 h-12 bg-accent/[0.06] rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none -z-10" />
        <div className="absolute bottom-0 left-0 w-12 h-12 bg-accent/[0.06] rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none -z-10" />
      </motion.div>
    );
  }
);

InteractiveCard.displayName = "InteractiveCard";
